import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, ActivityIndicator, TouchableOpacity, TextInput, Modal, ScrollView, Platform, Switch } from 'react-native';
import { Picker } from '@react-native-picker/picker';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import dayjs from 'dayjs';
import { getBusyPeriods, updateReservation } from '../../api/Reservation';
import { getPlantationsData, searchAddress } from '../../api/AllAddress ';
import { getBrandDetail } from '../../api/Price';
import { getProductFamiliesData, vehicleRentalProdLocation } from '../../api/Product';
import { getDiscountCodesData } from '../../api/Settings';
import { formatDate } from '../../common/utils/DateUtils';
import { getAddressLabel } from '../../common/utils/Helpers';
import { useAlertModal } from '../../common/hooks';

const toHHMMSS = (t?: string | null): string => {
  if (!t) return '';
  const ampm = String(t).match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (ampm) {
    let h = parseInt(ampm[1], 10);
    const period = ampm[4].toUpperCase();
    if (period === 'PM' && h < 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    return `${String(h).padStart(2, '0')}:${ampm[2]}:${ampm[3] || '00'}`;
  }
  const parts = String(t).split(':');
  if (parts.length === 2) return `${parts[0].padStart(2, '0')}:${parts[1]}:00`;
  if (parts.length === 3) return `${parts[0].padStart(2, '0')}:${parts[1]}:${parts[2]}`;
  return String(t);
};

const addMinutesToTime = (hhmmss: string, minutes: number): string => {
  const [h, m, s] = hhmmss.split(':').map((x) => parseInt(x, 10) || 0);
  const total = h * 60 + m + minutes;
  const nh = Math.floor((total % (24 * 60)) / 60);
  const nm = total % 60;
  return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const addDaysStr = (dateStr: string, days: number): string => {
  const [y, m, d] = dateStr.split('-').map((x) => parseInt(x, 10));
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  return formatDate(dt);
};

const addMonthsStr = (dateStr: string, months: number): string => {
  const [y, m, d] = dateStr.split('-').map((x) => parseInt(x, 10));
  const dt = new Date(y, m - 1, d);
  dt.setMonth(dt.getMonth() + months);
  return formatDate(dt);
};

const formatTime12 = (hhmmss?: string | null): string => {
  if (!hhmmss) return '';
  const [hStr, mStr] = String(hhmmss).split(':');
  let h = parseInt(hStr, 10);
  if (isNaN(h)) return '';
  const m = mStr || '00';
  const period = h >= 12 ? 'PM' : 'AM';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${m} ${period}`;
};

const fmtDateMDY = (ymd?: string): string => {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-');
  if (!d) return ymd;
  return `${m}/${d}/${y}`;
};

// Reader-friendly 'Aug 10, 2026' format for the greyed-dates / bookings panel.
const fmtDateNice = (ymd?: string): string => {
  if (!ymd) return '';
  const d = dayjs(ymd);
  return d.isValid() ? d.format('MMM D, YYYY') : ymd;
};

// Delivery area / address fields inside the reschedule modal are hidden per admin
// request (the reservation keeps its existing address). Flip to true to restore.
const SHOW_DELIVERY_ADDRESS_FIELDS = false;

// Reusable month grid used for BOTH the start-date and end-date (return) calendars.
// It renders a single month, highlights the selected [start, end] range, strikes
// through unavailable days, and disables days that can't be picked.
const CalendarGrid: React.FC<{
  viewMonth: dayjs.Dayjs;
  onPrev: () => void;
  onNext: () => void;
  prevDisabled?: boolean;
  showPrev?: boolean;
  showNext?: boolean;
  startDate: string;
  endDate: string;
  isSelectable: (ds: string) => boolean;
  isUnavailable: (ds: string) => boolean;
  onPick: (ds: string) => void;
  width?: number;
}> = ({ viewMonth, onPrev, onNext, prevDisabled, showPrev = true, showNext = true, startDate, endDate, isSelectable, isUnavailable, onPick, width = 320 }) => {
  const days = useMemo(() => {
    const start = viewMonth.startOf('month').startOf('week');
    const end = viewMonth.endOf('month').endOf('week');
    const arr: dayjs.Dayjs[] = [];
    let cur = start;
    while (cur.isBefore(end) || cur.isSame(end, 'day')) { arr.push(cur); cur = cur.add(1, 'day'); }
    return arr;
  }, [viewMonth]);

  return (
    <View style={{ width, borderWidth: 1, borderColor: '#e0e0e0', borderRadius: 8, padding: 16, backgroundColor: '#ffffff' }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        {showPrev ? (
          <TouchableOpacity disabled={prevDisabled} onPress={onPrev} style={{ paddingHorizontal: 8, paddingVertical: 4, opacity: prevDisabled ? 0.3 : 1 }}>
            <Text style={{ fontSize: 22, color: '#555555', lineHeight: 24 }}>{'‹'}</Text>
          </TouchableOpacity>
        ) : (
          <View style={{ width: 30 }} />
        )}
        <Text style={{ fontSize: 17, fontWeight: '500', color: '#333333' }}>{viewMonth.format('MMMM YYYY')}</Text>
        {showNext ? (
          <TouchableOpacity onPress={onNext} style={{ paddingHorizontal: 8, paddingVertical: 4 }}>
            <Text style={{ fontSize: 22, color: '#555555', lineHeight: 24 }}>{'›'}</Text>
          </TouchableOpacity>
        ) : (
          <View style={{ width: 30 }} />
        )}
      </View>

      <View style={{ flexDirection: 'row', marginBottom: 4 }}>
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <View key={i} style={{ flex: 1, alignItems: 'center', paddingVertical: 4 }}>
            <Text style={{ fontSize: 13, color: '#888888' }}>{d}</Text>
          </View>
        ))}
      </View>

      {Array.from({ length: days.length / 7 }).map((_, row) => (
        <View key={row} style={{ flexDirection: 'row' }}>
          {days.slice(row * 7, row * 7 + 7).map((d, ci) => {
            const inMonth = d.month() === viewMonth.month();
            if (!inMonth) return <View key={`empty-${ci}`} style={{ flex: 1, aspectRatio: 1 }} />;
            const key = d.format('YYYY-MM-DD');
            const selectable = isSelectable(key);
            const unavailable = isUnavailable(key);
            const isStart = !!startDate && key === startDate;
            const isEnd = !!endDate && key === endDate;
            const inRange = !!startDate && !!endDate && key > startDate && key < endDate;
            const isEdge = isStart || isEnd;
            const rangeActive = isEdge || inRange;
            return (
              <View
                key={key}
                style={{
                  flex: 1,
                  aspectRatio: 1,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: rangeActive ? '#a9dcdc' : 'transparent',
                  borderTopLeftRadius: isStart ? 999 : 0,
                  borderBottomLeftRadius: isStart ? 999 : 0,
                  borderTopRightRadius: isEnd ? 999 : 0,
                  borderBottomRightRadius: isEnd ? 999 : 0,
                }}
              >
                <TouchableOpacity
                  disabled={!selectable}
                  activeOpacity={0.7}
                  onPress={() => onPick(key)}
                  style={{ width: '92%', aspectRatio: 1, alignItems: 'center', justifyContent: 'center', borderRadius: 999, backgroundColor: isEdge ? '#1a8a8a' : 'transparent' }}
                >
                  <Text
                    style={{
                      fontSize: 15,
                      fontWeight: rangeActive ? '700' : '400',
                      color: isEdge ? '#ffffff' : inRange ? '#0c5b5a' : selectable ? '#333333' : '#bdbdbd',
                      textDecorationLine: unavailable && !isEdge ? 'line-through' : 'none',
                    }}
                  >
                    {d.format('D')}
                  </Text>
                </TouchableOpacity>
              </View>
            );
          })}
        </View>
      ))}
    </View>
  );
};

interface Props {
  details: any;
  setUpdateCount: (fn: (prev: number) => number) => void;
}

// Availability-backed schedule editor for vehicle reservations (pricing_system === 1).
// Reuses the same getBusyPeriods endpoint as customer checkout; passes
// exclude_reservation_id so the edited reservation doesn't conflict with itself.
const VehicleScheduleEditor = ({ details, setUpdateCount }: Props) => {
  const { showAlert } = useAlertModal();

  const items = useMemo(() => {
    if (Array.isArray(details?.items)) return details.items;
    if (typeof details?.items === 'string') {
      try { return JSON.parse(details.items); } catch { return []; }
    }
    return [];
  }, [details]);

  // Fulfillment type drives which pricing rows (fulfillment_type) the availability
  // API returns. Default from the reservation's delivery_method (0=Pickup, 1=Delivery),
  // robust to string/number. The admin can override it via the dropdown; once touched
  // we stop the auto-flip so the chosen type's pricing is respected.
  const initialOrderType = Number(details?.delivery_method) === 1 ? 'Delivery' : 'Pickup';
  const [orderType, setOrderType] = useState<string>(initialOrderType);
  const [orderTypeTouched, setOrderTypeTouched] = useState(false);

  // Delivery pricing depends on the area (plantation): getBusyPeriods derives the
  // delivery upcharge from plantation_id. Mirror the consumer flow's "Select Area"
  // so the admin can set/correct the area when pricing a delivery.
  // Discount codes so the modal can RE-derive the discount from the promo code
  // (percent of the new subtotal) exactly like checkout/ProceedReservation,
  // instead of reusing the stored flat amount from the old subtotal.
  const [discountCodes, setDiscountCodes] = useState<any[]>([]);
  useEffect(() => {
    getDiscountCodesData((jsonRes: any, status: any) => {
      if (status === 200 && Array.isArray(jsonRes)) setDiscountCodes(jsonRes);
    });
  }, []);

  const [plantations, setPlantations] = useState<any[]>([]);
  const [plantationId, setPlantationId] = useState<number | null>(
    details?.plantation_id ?? details?.all_addresses?.plantation_id ?? null
  );
  // Best-effort display string for the reservation's current delivery address.
  // Falls back to the saved `all_addresses` record and finally the plain
  // `delivery_address` text so a default always shows (mirrors ReservationMainInfo).
  const deliveryAddress = useMemo(() => {
    if (details?.use_manual) return details?.manual_address || details?.delivery_address || '';
    const a = details?.all_addresses;
    if (a) {
      const s = `${a.number ?? ''} ${a.street ?? ''} ${a.property_name ?? ''} ${a.plantation ?? ''}`.replace(/\s+/g, ' ').trim();
      if (s) return s;
    }
    return details?.delivery_address || '';
  }, [details]);

  // Editable delivery address (mirrors ReservationMainInfo / the consumer flow): the
  // admin can search a saved address or type a manual one. Only sent on Update when
  // touched. Selecting a saved address also sets plantation_id so pricing updates.
  const [addrTouched, setAddrTouched] = useState(false);
  const [useManual, setUseManual] = useState<boolean>(!!details?.use_manual);
  const [addressId, setAddressId] = useState<number | null>(details?.address_id ?? null);
  const [manualAddress, setManualAddress] = useState<string>(details?.manual_address || deliveryAddress);
  const [searchedAddresses, setSearchedAddresses] = useState<any[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<any>(null);
  const [searchKey, setSearchKey] = useState<string>(deliveryAddress);
  const searchTimer = useRef<any>(null);

  // Repopulate the address fields from the reservation whenever it (re)loads,
  // unless the admin has already edited them. This ensures the current delivery
  // address is shown by default even if `details` arrives/updates after mount.
  useEffect(() => {
    if (addrTouched) return;
    setUseManual(!!details?.use_manual);
    setAddressId(details?.address_id ?? null);
    setManualAddress(details?.use_manual ? (details?.manual_address || deliveryAddress) : '');
    setSearchKey(deliveryAddress);
  }, [details, deliveryAddress, addrTouched]);

  const getAddresses = async (q: string) => {
    setSelectedAddress(null);
    try {
      const response: any = await searchAddress(String(q).toLowerCase().trim(), details.brand_id);
      const items = await response.json();
      if (Array.isArray(items)) {
        const opts = items
          .map((address: any, index: number) => {
            const label = getAddressLabel(address, index);
            if (label === '') return null;
            return { ...address, label };
          })
          .filter(Boolean);
        setSearchedAddresses(opts as any[]);
      } else setSearchedAddresses([]);
    } catch {
      setSearchedAddresses([]);
    }
  };

  const onPickAddress = (_event: any, value: any) => {
    if (!value || typeof value === 'string') return;
    setSelectedAddress(value);
    setAddressId(value.id ?? null);
    setUseManual(false);
    setManualAddress('');
    if (value.plantation_id != null) setPlantationId(Number(value.plantation_id));
    setSearchKey(String(value.label || '').replace(/<[^>]*>?/gm, ''));
    setAddrTouched(true);
  };

  const families = useMemo(() => {
    const map = new Map<number, number>();
    items.forEach((it: any) => {
      if (it.family_id == null) return;
      map.set(it.family_id, (map.get(it.family_id) || 0) + (Number(it.quantity) || 1));
    });
    return Array.from(map.entries()).map(([family_id, quantity]) => ({ family_id, quantity }));
  }, [items]);

  // family_id -> readable item name, so inventory-based unavailability can name the
  // specific booked-out item(s).
  const familyName = useMemo(() => {
    const m = new Map<number, string>();
    items.forEach((it: any) => {
      if (it.family_id == null || m.has(it.family_id)) return;
      m.set(it.family_id, it.display_name || it.name || it.product_name || `item #${it.family_id}`);
    });
    return m;
  }, [items]);

  // Brand of the order plus the units physically AT this brand's locations right
  // now (same source as the Inventory screen: product/vehicleRentalProdLocation
  // counts product_products.current_location against the brand's store locations).
  const [brandName, setBrandName] = useState<string>('');
  const [locationQty, setLocationQty] = useState<Record<number, number | null>>({});

  useEffect(() => {
    if (!details?.brand_id) return;
    getBrandDetail({ id: details.brand_id }, (jsonRes: any, status: any) => {
      if (status == 200) setBrandName(jsonRes?.brand || '');
    });
  }, [details?.brand_id]);

  useEffect(() => {
    if (!details?.brand_id || families.length === 0) return;
    let cancelled = false;
    // Reservation items don't carry category_id, so resolve it via the product
    // families list, then query location-based qty once per distinct category.
    getProductFamiliesData(0, (pfRes: any, pfStatus: any) => {
      if (cancelled || pfStatus != 200 || !Array.isArray(pfRes)) return;
      const catByFamily = new Map<number, number>();
      pfRes.forEach((pf: any) => catByFamily.set(Number(pf.id), Number(pf.category_id)));
      const cats = Array.from(new Set(
        families.map((f) => catByFamily.get(f.family_id)).filter((c): c is number => !!c)
      ));
      cats.forEach((categoryId) => {
        vehicleRentalProdLocation({ categoryId, brandId: details.brand_id }, (rows: any, status: any) => {
          if (cancelled || status != 200 || !Array.isArray(rows)) return;
          setLocationQty((prev) => {
            const out = { ...prev };
            rows.forEach((row: any) => {
              const qty = Number(row?.available_qty_at_locations?.[0]?.available_qty);
              const fids = String(row.familiesIds ?? row.id ?? '').split(',');
              fids.forEach((fid: string) => {
                const n = Number(fid);
                if (n && families.some((f) => f.family_id === n)) out[n] = isNaN(qty) ? null : qty;
              });
            });
            return out;
          });
        });
      });
    });
    return () => { cancelled = true; };
  }, [details?.brand_id, JSON.stringify(families)]); // eslint-disable-line react-hooks/exhaustive-deps

  // Start time stays as originally booked; start DATE is selectable but limited
  // to dates with inventory availability. Hourly is intentionally ignored.
  const initialStart = useMemo(
    () => (details?.start_date ? formatDate(new Date(`${details.start_date} 00:00:00`)) : formatDate(new Date())),
    [details?.start_date]
  );
  const startTime24 = useMemo(() => toHHMMSS(details?.start_time) || '09:00:00', [details?.start_time]);
  const origEndDate = useMemo(
    () => (details?.end_date ? formatDate(new Date(`${details.end_date} 00:00:00`)) : initialStart),
    [details?.end_date, initialStart]
  );
  const origEndTime = useMemo(() => toHHMMSS(details?.end_time) || '', [details?.end_time]);
  const originalTotal = useMemo(() => Number(details?.total_price) || 0, [details?.total_price]);
  // Current rental length in days (pickup date -> return date), used to label
  // the duration chips so the admin sees what the booking currently is.
  const originalDays = useMemo(() => {
    const d = dayjs(origEndDate).startOf('day').diff(dayjs(initialStart).startOf('day'), 'day');
    return d > 0 ? d : 1;
  }, [origEndDate, initialStart]);

  const [datesByFamily, setDatesByFamily] = useState<Record<number, Record<string, any>>>({});
  const [dailyReturnTime, setDailyReturnTime] = useState('09:00');
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState<string>(initialStart);
  // User-selected return date. The rental occupies [startDate, endDate) and is
  // returned on endDate morning; pricing derives from the day-count of this range.
  const [endDate, setEndDate] = useState<string>(origEndDate);
  // The dual-month range picker shows two consecutive months: viewMonth (left) and
  // viewMonth+1 (right). A single chevron pair pages both together.
  const [viewMonth, setViewMonth] = useState(() => dayjs(initialStart));
  const rightMonth = viewMonth.add(1, 'month');
  // Two-handle range selection: with a full range shown, tapping LEFT of it moves
  // the START handle, RIGHT of it moves the RETURN handle, inside moves the nearest
  // edge. endDate === '' means a fresh anchor is waiting for its return date.
  const [applying, setApplying] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  // Hourly-mode selection: when set, the reschedule is a same-day intra-day
  // rental at this tier (endDate === startDate; end_time = start_time + N hours).
  // Cleared as soon as the admin touches the calendar or a day/week/month chip.
  const [hourlyDur, setHourlyDur] = useState<{ id: string; value: number } | null>(null);

  // Re-sync with the booking whenever it reloads (e.g. right after an update) or
  // the modal reopens: anchor the CURRENT start in return mode, show its months.
  useEffect(() => {
    setStartDate(initialStart);
    setEndDate(origEndDate);
    setViewMonth(dayjs(initialStart));
    setHourlyDur(null);
  }, [initialStart, origEndDate, modalVisible]);
  // Admin override: bypass past-date / cut-off / blockout gating in getBusyPeriods so
  // those dates become selectable. Real inventory availability is still enforced.
  const [adminOverride, setAdminOverride] = useState(false);
  // Apply the configured 'Extra Day' rate (vr_prices daily_0) for each additional day
  // beyond the largest matching tier. Ticked by default; unticking falls back to the
  // legacy per-day rate derived from the smallest tier.
  const [useExtraDay, setUseExtraDay] = useState(true);
  const familiesSig = JSON.stringify(families);

  // Fetch availability for the currently visible month, mirroring the consumer
  // frontend: current month -> today..end-of-month, other months -> 1st..last.
  // Results merge into datesByFamily so navigating months fills in coverage.
  const monthKey = viewMonth.format('YYYY-MM');
  const rightMonthKey = rightMonth.format('YYYY-MM');

  useEffect(() => {
    if (families.length === 0 || !details?.brand_id) return;

    // Months we need data for, deduped:
    //  - the viewed month, so the calendar can render (skipped only if it's a fully
    //    past month with the override off, since nothing there is selectable), and
    //  - the SELECTED start date's month, so its pricing panel always has data even
    //    when the admin has paged the calendar to a different month.
    const months: dayjs.Dayjs[] = [];
    const pushMonth = (m: dayjs.Dayjs) => {
      if (!months.some((x) => x.isSame(m, 'month'))) months.push(m.startOf('month'));
    };
    if (adminOverride || !viewMonth.endOf('month').isBefore(dayjs(), 'day')) pushMonth(viewMonth);
    // The right-hand (viewMonth + 1) calendar's month, plus the selected start/return
    // months, so range availability and pricing work across month boundaries.
    if (adminOverride || !rightMonth.endOf('month').isBefore(dayjs(), 'day')) pushMonth(rightMonth);
    if (startDate) pushMonth(dayjs(startDate));
    if (endDate) pushMonth(dayjs(endDate));
    if (months.length === 0) return;

    // Fetch range for a given month. The current month starts at today (consumer
    // behaviour) unless the override is on — but never clip past the selected start
    // date if it lives in that month, so its pricing entry is always included.
    const rangeFor = (m: dayjs.Dayjs) => {
      const isCurrentMonth = m.isSame(dayjs(), 'month');
      let start = (isCurrentMonth && !adminOverride) ? dayjs().startOf('day') : m.startOf('month');
      if (startDate && dayjs(startDate).isSame(m, 'month') && dayjs(startDate).isBefore(start, 'day')) {
        start = dayjs(startDate).startOf('day');
      }
      return { startStr: start.format('YYYY-MM-DD'), endStr: m.endOf('month').format('YYYY-MM-DD') };
    };

    let cancelled = false;
    setLoading(true);

    // Fetch every needed month for one order type, merging into a single per-family
    // { date -> entry } map.
    const fetchForType = async (ot: string) => {
      const dbf: Record<number, Record<string, any>> = {};
      families.forEach((f) => { dbf[f.family_id] = {}; });
      let returnTime = '09:00';
      await Promise.all(
        months.map((m) => {
          const { startStr, endStr } = rangeFor(m);
          return Promise.all(
            families.map(
              (f) =>
                new Promise<void>((resolve) => {
                  getBusyPeriods(
                    {
                      brand_id: details.brand_id,
                      product_ids: [f.family_id],
                      quantity: f.quantity,
                      order_type: ot,
                      start_date: startStr,
                      end_date: endStr,
                      selected_date: startStr,
                      exclude_reservation_id: details.id,
                      plantation_id: ot === 'Delivery' ? (plantationId ?? null) : (details.plantation_id ?? null),
                      admin_override: adminOverride,
                    },
                    (jsonRes: any, status: any) => {
                      if (status === 200 && jsonRes?.success) {
                        returnTime = jsonRes.daily_return_time || returnTime;
                        (jsonRes.dates || []).forEach((d: any) => { dbf[f.family_id][d.date] = d; });
                      }
                      resolve();
                    }
                  );
                })
            )
          );
        })
      );
      return { dbf, returnTime };
    };

    // No dates for EVERY family means the requested order_type has no pricing
    // rows at all; retry with the opposite type since delivery_method can be
    // ambiguous on the reservation.
    const noPricing = (dbf: Record<number, Record<string, any>>) =>
      families.every((f) => Object.keys(dbf[f.family_id] || {}).length === 0);

    (async () => {
      let { dbf, returnTime } = await fetchForType(orderType);
      // Only auto-detect the opposite type while the admin hasn't explicitly picked
      // one; if they have, respect their choice even when it has no pricing.
      if (!cancelled && noPricing(dbf) && !orderTypeTouched) {
        const flipped = orderType === 'Delivery' ? 'Pickup' : 'Delivery';
        const alt = await fetchForType(flipped);
        if (!noPricing(alt.dbf)) {
          dbf = alt.dbf;
          returnTime = alt.returnTime;
          if (!cancelled) setOrderType(flipped); // reflect the type that actually has pricing
        }
      }
      if (cancelled) return;
      setDailyReturnTime(returnTime);
      // Merge these months' date entries into the accumulated map.
      setDatesByFamily((prev) => {
        const out: Record<number, Record<string, any>> = { ...prev };
        Object.keys(dbf).forEach((fid) => {
          out[Number(fid)] = { ...(prev[Number(fid)] || {}), ...dbf[Number(fid)] };
        });
        return out;
      });
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [familiesSig, details?.brand_id, details?.id, orderType, orderTypeTouched, plantationId, monthKey, rightMonthKey, adminOverride, startDate, endDate]);

  // Pricing/availability is per fulfillment type, so drop cached month data when the
  // order type changes to avoid mixing Pickup and Delivery prices across months.
  // Toggling the admin override also changes which dates are available, so refresh too.
  useEffect(() => {
    setDatesByFamily({});
    // NOTE: we intentionally do NOT re-enable the auto default-pick here. Changing
    // the order type, delivery area, or override toggle only refreshes availability;
    // the admin's currently selected start date must stay put until they pick another.
  }, [orderType, plantationId, adminOverride]);

  // Load the area/plantation list (same source as the consumer 'Select Area').
  useEffect(() => {
    getPlantationsData(details?.store_belong_to, (jsonRes: any, status: any) => {
      setPlantations(status === 200 && Array.isArray(jsonRes) ? jsonRes : []);
    });
  }, [details?.store_belong_to]);

  // Delivery pricing = vr_prices tier + the area's cr_delivery_charge, and the
  // backend only applies that upcharge when plantation_id is sent. Reservations
  // with a manual/typed address have no plantation_id, which understates every
  // price here (and shows a phantom credit). Best effort: match an area name
  // inside the saved address text and use it.
  useEffect(() => {
    if (plantationId != null || plantations.length === 0) return;
    const text = `${details?.all_addresses?.plantation ?? ''} ${deliveryAddress}`.toLowerCase();
    if (!text.trim()) return;
    // Area names carry prefixes/suffixes the address never has (e.g. the area is
    // "CHS Kiawah Island" while the address just says "... Kiawah"), so compare
    // by DISTINCTIVE words: any non-generic word of the area name found as a
    // whole word in the address counts as a match. Longest word wins to avoid
    // ambiguity between similarly named areas.
    const GENERIC = new Set(['chs', 'island', 'islands', 'isle', 'beach', 'house', 'mount', 'west', 'north', 'south', 'east', 'other', 'priced', 'after', 'booking', 'the', 'of', 'and']);
    const addressWords = new Set(text.split(/[^a-z]+/).filter(Boolean));
    let best: any = null;
    let bestLen = 0;
    plantations.forEach((p: any) => {
      const words = String(p?.plantation || '').toLowerCase().split(/[^a-z]+/).filter((w) => w.length >= 4 && !GENERIC.has(w));
      words.forEach((w) => {
        if (addressWords.has(w) && w.length > bestLen) { best = p; bestLen = w.length; }
      });
    });
    if (best) setPlantationId(Number(best.id));
  }, [plantations, plantationId, deliveryAddress]); // eslint-disable-line react-hooks/exhaustive-deps

  // True when we CANNOT price the delivery upcharge: prices shown would exclude
  // the area delivery charge, so paid-vs-new comparisons would be misleading.
  const missingDeliveryArea = orderType === 'Delivery' && plantationId == null;

  // Dropdown label with the area's upcharge so the admin sees exactly what each
  // area adds to every duration price (mirrors getBusyPeriods: Delivery uses
  // cr_delivery_charge, Pickup uses partner_delivery_charge).
  const areaLabel = (p: any) => {
    const charge = parseFloat(orderType === 'Pickup' ? p?.partner_delivery_charge : p?.cr_delivery_charge) || 0;
    return charge > 0 ? `${p.plantation} — +$${charge.toFixed(2)} delivery` : `${p.plantation} — no upcharge`;
  };

  // Rental duration rows returned by the availability API, matching the consumer
  // frontend's DurationSelector, which lists ALL duration types (hourly, daily,
  // weekly, monthly). Hourly tiers are same-day; the day-tier pricing math in
  // `itemPricing` filters them out via a `days > 0` guard, so including them
  // here only affects the informational chip list, not date-range pricing.
  const rentalDurs = (entry: any) =>
    (entry?.durations || []).filter((x: any) => {
      const t = String(x.duration_type).toLowerCase();
      return t === 'hourly' || t === 'daily' || t === 'weekly' || t === 'monthly';
    });

  const durKey = (d: any) => `${Number(d.duration_value)}-${String(d.duration_type).toLowerCase()}`;

  // End date for a duration from a start date, matching the frontend exactly:
  // hourly = same day (intra-day rental), daily = +value days, weekly =
  // +(value*7) days, monthly = +value calendar months.
  const durEndDate = (ds: string, d: any) => {
    const t = String(d.duration_type).toLowerCase();
    const v = Number(d.duration_value) || 0;
    if (t === 'hourly') return ds;
    return t === 'monthly' ? addMonthsStr(ds, v) : addDaysStr(ds, t === 'weekly' ? v * 7 : v);
  };

  const durDays = (ds: string, d: any) =>
    dayjs(durEndDate(ds, d)).startOf('day').diff(dayjs(ds).startOf('day'), 'day');

  // Dates selectable as a START. Mirrors the consumer frontend exactly: a date
  // is selectable iff every family's entry is is_available (the frontend's
  // disabledDates only checks is_available — no qty / daily-row gating here).
  // With the admin override ON, date-rule blocks (past / cut-off / blockout) are
  // bypassed — any date that has a data entry is treated as a valid start candidate.
  // Real inventory availability is still enforced separately in isRangeAvailable.
  const availableStartDates = useMemo(() => {
    if (families.length === 0) return [];
    const first = datesByFamily[families[0].family_id] || {};
    return Object.keys(first)
      .sort()
      .filter((ds) =>
        families.every((f) => {
          const e = (datesByFamily[f.family_id] || {})[ds];
          if (!e) return false;
          if (adminOverride) return true;
          return e.is_available !== false;
        })
      );
  }, [datesByFamily, families, adminOverride]);

  // NOTE: we deliberately do NOT auto-snap the selection to the first available
  // date. The editor always opens on the CURRENT booking's range (even when it's in
  // the past / unavailable), so the admin sees the booking's own months and dates;
  // the reason panel explains any restriction and they can pick new dates from there.

  // Consecutive-day options from the selected start date. Each option carries
  // the intersected availability + summed subtotal across item families.
  const dayOptions = useMemo(() => {
    if (families.length === 0 || !startDate) return [];
    const startEntry = (datesByFamily[families[0].family_id] || {})[startDate];
    if (!startEntry) return [];
    // Distinct durations (value + type) at the start date, deduped like the frontend.
    const seen = new Map<string, any>();
    rentalDurs(startEntry).forEach((d: any) => {
      const id = durKey(d);
      if (!seen.has(id)) seen.set(id, d);
    });
    const opts = Array.from(seen.values()).map((d: any) => {
      const id = durKey(d);
      let availableAll = true;
      let subtotal = 0;
      families.forEach((f) => {
        const e = (datesByFamily[f.family_id] || {})[startDate];
        const rec = rentalDurs(e).find((x: any) => durKey(x) === id);
        if (!rec || rec.is_available === false) availableAll = false;
        if (rec) subtotal += (parseFloat(rec.price) || 0) * f.quantity;
      });
      return {
        id,
        type: String(d.duration_type).toLowerCase(),
        value: Number(d.duration_value),
        days: durDays(startDate, d),
        endDate: durEndDate(startDate, d),
        label: d.duration_label || `${d.duration_value} ${d.duration_type}`,
        subtotal,
        // Admin override: the toggle exists precisely so the admin can bypass
        // date/cut-off gating on the tiers too — otherwise a duration coming
        // back "unavailable" from the availability API (e.g. same-day hourly
        // past the cut-off) would still be unclickable even with override on.
        is_available: availableAll || adminOverride,
      };
    });
    return opts.sort((a, b) => a.days - b.days);
  }, [datesByFamily, families, startDate, adminOverride]);

  const dayAvailable = dayOptions.length > 0;

  // Explain WHY a specific date is unavailable, using the per-date reason flags from
  // getBusyPeriods. Returns null when the date is available (or has no data yet).
  const explainDate = (ds: string): string | null => {
    if (!ds || families.length === 0) return null;
    const entry = (datesByFamily[families[0].family_id] || {})[ds];
    if (!entry || entry.is_available) return null;
    // The override bypasses the date-rule blocks, so only surface them when it's off.
    if (!adminOverride && entry.is_past) return `${fmtDateMDY(ds)} has already passed.`;
    if (!adminOverride && entry.blocked_by_cutoff) return `The same-day cut-off time has passed, so ${fmtDateMDY(ds)} can no longer be booked.`;
    if (!adminOverride && entry.blocked_by_blockout) {
      return `${fmtDateMDY(ds)} is within a blockout period${entry.blockout_end_date ? ` (blocked through ${fmtDateMDY(entry.blockout_end_date)})` : ''}.`;
    }
    // Otherwise it's an inventory shortage: name each item family that doesn't have
    // enough units left for the requested quantity, with booked/total counts.
    const short = families
      .map((f) => {
        const e = (datesByFamily[f.family_id] || {})[ds];
        if (!e) return null;
        const remaining = Number(e.remaining_quantity ?? 0);
        if (remaining >= f.quantity) return null;
        return {
          name: familyName.get(f.family_id) || `item #${f.family_id}`,
          remaining,
          need: f.quantity,
          total: Number(e.total_quantity ?? 0),
          booked: Number(e.booked_quantity ?? 0),
        };
      })
      .filter(Boolean) as Array<{ name: string; remaining: number; need: number; total: number; booked: number }>;
    if (short.length) {
      const parts = short.map((s) =>
        s.remaining <= 0
          ? `${s.name} (all ${s.total} booked)`
          : `${s.name} (only ${s.remaining} of ${s.total} left${s.need > 1 ? `, need ${s.need}` : ''})`
      );
      return `${fmtDateMDY(ds)} doesn't have enough inventory — ${parts.join('; ')}.`;
    }
    return `${fmtDateMDY(ds)} is unavailable.`;
  };

  // Structured reason for why a multi-day rental over [start, end] is unavailable:
  // the item that runs out, the exact blocked date range, and the conflicting
  // order(s) with their full start/end date-times — so the UI can render a table.
  const getRentalConflict = (start: string, end: string) => {
    const blocked: string[] = [];
    const orders = new Map<string, { orderNumber: string; from: string; fromTime?: string; to: string; toTime?: string }>();
    let itemName = '';
    let cur = dayjs(start);
    const last = dayjs(end);
    while (cur.isBefore(last) || cur.isSame(last, 'day')) {
      const ds = cur.format('YYYY-MM-DD');
      for (const f of families) {
        const e = (datesByFamily[f.family_id] || {})[ds];
        if (!e) continue;
        if (Number(e.remaining_quantity ?? 0) < f.quantity) {
          if (!blocked.includes(ds)) blocked.push(ds);
          if (!itemName) itemName = familyName.get(f.family_id) || `item #${f.family_id}`;
          (e.booked_ranges || []).forEach((r: any) => {
            if (r.type === 'reservation' && r.order_number && !orders.has(r.order_number)) {
              orders.set(r.order_number, {
                orderNumber: r.order_number,
                from: r.res_start || ds,
                fromTime: r.res_start_time,
                to: r.res_end || ds,
                toTime: r.res_end_time,
              });
            }
          });
        }
      }
      cur = cur.add(1, 'day');
    }
    if (blocked.length === 0) return null;
    const blockedRange = blocked.length === 1
      ? fmtDateMDY(blocked[0])
      : `${fmtDateMDY(blocked[0])} – ${fmtDateMDY(blocked[blocked.length - 1])}`;
    return { itemName: itemName || 'this vehicle', blockedRange, orders: Array.from(orders.values()) };
  };

  // The action hint appended to an unavailability message.
  const nextActionHint = (from: string): string => {
    const nextAvail = availableStartDates.find((d) => d > from);
    if (nextAvail) return ` Next available booking date: ${fmtDateMDY(nextAvail)}.`;
    if (!adminOverride) return ' Turn on "Override date restrictions" above to book restricted dates anyway.';
    return ' No available booking dates for this vehicle in the next 90 days.';
  };

  // Calendar: fast lookup of selectable dates + the visible month's day grid.
  const availableSet = useMemo(() => new Set(availableStartDates), [availableStartDates]);

  // The order's ORIGINAL pickup date is always a valid anchor — even when it's in
  // the past — so the admin can keep/extend the booking's own start without the
  // override. Its units are excluded from the availability query anyway.
  const isStartCandidate = (ds: string): boolean => availableSet.has(ds) || ds === initialStart;

  // A day within a rental span only needs INVENTORY. Date rules (past / cut-off /
  // blockout) gate where a rental can START, not where it continues — the customer
  // already has the vehicle on mid-days, so only unit availability matters here.
  const dayHasInventory = (ds: string): boolean =>
    families.length > 0 &&
    families.every((f) => {
      const e = (datesByFamily[f.family_id] || {})[ds];
      if (!e) return false;
      return Number(e.remaining_quantity ?? 0) >= f.quantity;
    });

  // A [start, end) range is bookable iff every occupied day (start .. end-1) is usable.
  const isRangeAvailable = (start: string, end: string): boolean => {
    if (!start || !end || end <= start) return false;
    let cur = dayjs(start);
    const lastOccupied = dayjs(end).subtract(1, 'day');
    while (cur.isBefore(lastOccupied) || cur.isSame(lastOccupied, 'day')) {
      if (!dayHasInventory(cur.format('YYYY-MM-DD'))) return false;
      cur = cur.add(1, 'day');
    }
    return true;
  };

  // Price for an arbitrary day-count at the start date: an exact tier match uses the
  // tier price; otherwise take the LARGEST tier that fits and add each additional day
  // at the configured 'Extra Day' rate (when ticked and set), falling back to the
  // 1-day-derived rate. Returns the unit price and the base tier's price_id.
  // Also returns the breakdown (base tier vs extra-day surcharge) so the UI can
  // surface "tier + N extra days" when the range doesn't match a tier exactly.
  const itemPricing = (
    familyId: number,
    days: number
  ): { unit: number; price_id: any; baseDays: number; basePrice: number; extraDays: number; perExtraDay: number } => {
    const e = (datesByFamily[familyId] || {})[startDate];
    const durs = rentalDurs(e)
      .map((d: any) => ({ days: durDays(startDate, d), price: parseFloat(d.price) || 0, price_id: d.price_id }))
      .filter((x: any) => x.days > 0)
      .sort((a: any, b: any) => a.days - b.days);
    if (durs.length === 0) return { unit: 0, price_id: null, baseDays: 0, basePrice: 0, extraDays: 0, perExtraDay: 0 };
    const exact = durs.find((x: any) => x.days === days);
    if (exact) {
      return { unit: Math.round(exact.price * 100) / 100, price_id: exact.price_id, baseDays: days, basePrice: exact.price, extraDays: 0, perExtraDay: 0 };
    }
    const smallest = durs[0];
    const subTierPerDay = smallest.price / smallest.days; // normally the 1-day price
    const configuredExtra = parseFloat(e?.extra_day_price) || 0;
    const perExtraDay = useExtraDay && configuredExtra > 0 ? configuredExtra : subTierPerDay;
    const base = [...durs].reverse().find((x: any) => x.days <= days);
    if (!base) {
      // Shorter than the smallest tier: charge per-day from the smallest tier.
      return { unit: Math.round(subTierPerDay * days * 100) / 100, price_id: smallest.price_id, baseDays: 0, basePrice: 0, extraDays: 0, perExtraDay: 0 };
    }
    const unit = base.price + (days - base.days) * perExtraDay;
    return { unit: Math.round(unit * 100) / 100, price_id: base.price_id, baseDays: base.days, basePrice: base.price, extraDays: days - base.days, perExtraDay };
  };

  // Displayed 'Extra Day' rate: the configured per-day surcharge at the selected
  // start date, summed across item families × quantity (matches subtotal math).
  const extraDayRate = useMemo(() => {
    let sum = 0;
    families.forEach((f) => {
      const e = (datesByFamily[f.family_id] || {})[startDate];
      sum += (parseFloat(e?.extra_day_price) || 0) * f.quantity;
    });
    return Math.round(sum * 100) / 100;
  }, [datesByFamily, familiesSig, startDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Breakdown of the CURRENT range when it doesn't match a tier exactly: the base
  // tier portion plus the extra-day surcharge, aggregated across families × qty.
  // Null when the selection lands exactly on a tier (nothing extra to explain).
  const rangeBreakdown = useMemo(() => {
    if (!startDate || !endDate || families.length === 0) return null;
    const days = dayjs(endDate).startOf('day').diff(dayjs(startDate).startOf('day'), 'day');
    if (days <= 0) return null;
    let baseSubtotal = 0;
    let extraAmount = 0;
    let baseDays = 0;
    let extraDays = 0;
    let hasExtra = false;
    families.forEach((f) => {
      const p = itemPricing(f.family_id, days);
      if (p.extraDays > 0) {
        hasExtra = true;
        baseDays = Math.max(baseDays, p.baseDays);
        extraDays = Math.max(extraDays, p.extraDays);
        baseSubtotal += p.basePrice * f.quantity;
        extraAmount += p.perExtraDay * p.extraDays * f.quantity;
      } else {
        baseSubtotal += p.unit * f.quantity;
      }
    });
    if (!hasExtra) return null;
    return {
      baseDays,
      extraDays,
      baseSubtotal: Math.round(baseSubtotal * 100) / 100,
      extraAmount: Math.round(extraAmount * 100) / 100,
    };
  }, [startDate, endDate, datesByFamily, familiesSig, useExtraDay]); // eslint-disable-line react-hooks/exhaustive-deps

  // Total rental subtotal for `days` across all families (tier + extra days at 1-day rate).
  const priceForDays = (days: number): number => {
    if (days <= 0) return 0;
    let subtotal = 0;
    families.forEach((f) => { subtotal += itemPricing(f.family_id, days).unit * f.quantity; });
    return Math.round(subtotal * 100) / 100;
  };

  useEffect(() => {
    if (startDate) setViewMonth((m) => (m.isSame(dayjs(startDate), 'month') ? m : dayjs(startDate)));
  }, [startDate]);

  // Keep a NON-EMPTY return date valid relative to the start date. We only fix up a
  // stale end that lands on/before the new start; an empty end means the admin is
  // mid-selection (picking the return date), so leave it alone.
  useEffect(() => {
    if (!startDate) return;
    if (endDate && endDate <= startDate) {
      setEndDate(addDaysStr(startDate, Math.max(1, originalDays)));
    }
  }, [startDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // What's tappable: while a fresh anchor waits for its return, valid returns and
  // re-anchor candidates; with a full range, anything that can move a handle plus
  // re-anchor candidates. Days inside the range always shrink one of the edges.
  const isDateSelectable = (ds: string): boolean => {
    // Hourly mode: pure single-date picker. Only "is this a valid start" matters.
    if (hourlyDur) return isStartCandidate(ds);
    if (!endDate) return isStartCandidate(ds) || (ds > startDate && isRangeAvailable(startDate, ds));
    if (ds < startDate) return isStartCandidate(ds);
    if (ds > endDate) return isRangeAvailable(startDate, ds) || isStartCandidate(ds);
    return true;
  };
  // Visual grey/strike: ONLY days that are genuinely unbookable (past / cut-off /
  // blockout / fully booked). A free day is never struck just because booked days
  // sit between it and the current selection — tapping it re-anchors the range there.
  const isDateUnavailable = (ds: string): boolean => {
    // Hourly mode: single-date semantics — a date is unavailable iff it can't
    // be a start. No range/adjacency reasoning applies.
    if (hourlyDur) return !isStartCandidate(ds);
    if (!endDate) {
      if (ds <= startDate) return !isStartCandidate(ds);
      return !(isRangeAvailable(startDate, ds) || isStartCandidate(ds));
    }
    if (ds < startDate) return !isStartCandidate(ds);
    if (ds > endDate) return !(isRangeAvailable(startDate, ds) || isStartCandidate(ds));
    return false;
  };

  // All bookings that occupy any day within the two visible months — shown in the
  // always-visible third panel so the admin can see WHY days are greyed out.
  const visibleBookings = useMemo(() => {
    if (families.length === 0) return [];
    const map = new Map<string, { orderNumber: string; item: string; qty: number; from: string; fromTime?: string; to: string; toTime?: string; familyId: number; need: number }>();
    const last = rightMonth.endOf('month');
    let cur = viewMonth.startOf('month');
    while (cur.isBefore(last) || cur.isSame(last, 'day')) {
      const ds = cur.format('YYYY-MM-DD');
      families.forEach((f) => {
        const e = (datesByFamily[f.family_id] || {})[ds];
        const ranges = (e?.booked_ranges || []).filter((r: any) => r.type === 'reservation' && r.order_number);
        ranges.forEach((r: any) => {
          if (map.has(r.order_number)) return;
          // An order may appear as several item rows (ranges) on the SAME day —
          // sum those for the true booked quantity. Repeats on later days are
          // the same range again, so only the first encountered day counts.
          const qty = ranges
            .filter((x: any) => x.order_number === r.order_number)
            .reduce((s: number, x: any) => s + (Number(x.quantity) || 1), 0);
          map.set(r.order_number, {
            orderNumber: r.order_number,
            item: familyName.get(f.family_id) || `item #${f.family_id}`,
            qty,
            from: r.res_start || ds,
            fromTime: r.res_start_time,
            to: r.res_end || ds,
            toTime: r.res_end_time,
            familyId: f.family_id,
            need: f.quantity,
          });
        });
      });
      cur = cur.add(1, 'day');
    }
    const others = Array.from(map.values()).sort((a, b) => (a.from < b.from ? -1 : 1));
    // The edited order itself is EXCLUDED from the API response (so its own dates
    // don't block the reschedule) — list it explicitly when it touches these months.
    const cur0 = details?.start_date && details?.end_date ? {
      orderNumber: String(details.order_number || details.id),
      item: Array.from(new Set(families.map((f) => familyName.get(f.family_id) || `item #${f.family_id}`))).join(', '),
      qty: families.reduce((s, f) => s + f.quantity, 0),
      from: dayjs(details.start_date).format('YYYY-MM-DD'),
      fromTime: details.start_time,
      to: dayjs(details.end_date).format('YYYY-MM-DD'),
      toTime: details.end_time,
      familyId: families[0]?.family_id ?? 0,
      need: families[0]?.quantity ?? 1,
      isCurrent: true,
    } : null;
    const touches = cur0 && !(dayjs(cur0.to).isBefore(viewMonth.startOf('month'), 'day') || dayjs(cur0.from).isAfter(last, 'day'));
    return touches && cur0 ? [cur0, ...others] : others;
  }, [datesByFamily, families, familyName, monthKey, details?.start_date, details?.end_date]); // eslint-disable-line react-hooks/exhaustive-deps
  // Plain-language availability summary for the two visible months: consecutive
  // OPEN dates (bookable right now) and consecutive unavailable dates with a
  // simple reason each — rendered in the third panel.
  const availability = useMemo(() => {
    const open: { from: string; to: string }[] = [];
    const closed: { from: string; to: string; reason: string }[] = [];
    if (families.length === 0) return { open, closed };
    const first = datesByFamily[families[0].family_id] || {};
    const todayStr = dayjs().format('YYYY-MM-DD');
    const reasonOf = (ds: string): string | null => {
      const e = first[ds];
      // Past days of the current month aren't fetched (the query starts at today),
      // yet they render struck — still explain them.
      if (!e) return !adminOverride && ds < todayStr ? 'date already passed' : null;
      if (e.is_available === false) {
        // Inventory shortage first — it applies regardless of the override.
        const short = families.some((f) => {
          const fe = (datesByFamily[f.family_id] || {})[ds];
          return fe && Number(fe.remaining_quantity ?? 0) < f.quantity;
        });
        // Name the exact orders that use up the units on this day — the insight
        // lives right next to the date instead of in badges on the order cards.
        const takenBy = () => {
          const nums = Array.from(new Set(
            ((e.booked_ranges || []) as any[])
              .filter((r: any) => r.type === 'reservation' && r.order_number)
              .map((r: any) => `#${r.order_number}`)
          ));
          return nums.length ? `fully booked by ${nums.join(' + ')}` : 'fully booked — no vehicles left';
        };
        if (short) return takenBy();
        if (e.is_past) return 'date already passed';
        if (e.blocked_by_cutoff) return 'past the same-day cut-off time';
        if (e.blocked_by_blockout) return 'blocked out by the store';
        // Fall back to what actually occupies the day per the busy ranges.
        const ranges = e.booked_ranges || [];
        if (ranges.some((r: any) => r.type === 'reservation')) return takenBy();
        if (ranges.some((r: any) => r.type === 'blockout')) return 'blocked out by the store';
        return 'no inventory for this item';
      }
      return null;
    };
    const last = rightMonth.endOf('month');
    let cur = viewMonth.startOf('month');
    while (cur.isBefore(last) || cur.isSame(last, 'day')) {
      const ds = cur.format('YYYY-MM-DD');
      const r = reasonOf(ds);
      if (r) {
        const g = closed[closed.length - 1];
        if (g && g.reason === r && dayjs(g.to).add(1, 'day').format('YYYY-MM-DD') === ds) g.to = ds;
        else closed.push({ from: ds, to: ds, reason: r });
      } else if (first[ds]) {
        // Has data and no blocking reason → genuinely bookable right now.
        const g = open[open.length - 1];
        if (g && dayjs(g.to).add(1, 'day').format('YYYY-MM-DD') === ds) g.to = ds;
        else open.push({ from: ds, to: ds });
      }
      cur = cur.add(1, 'day');
    }
    return { open, closed };
  }, [datesByFamily, families, adminOverride, monthKey, startDate, endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const onPickDate = (ds: string) => {
    // Hourly mode: same-day rental — the calendar acts as a single-date picker.
    // Just move the start anchor; return date stays === start (hourly derives
    // the return time from it). No range selection here.
    if (hourlyDur) {
      if (ds !== startDate && isStartCandidate(ds)) setStartDate(ds);
      return;
    }
    // Fresh anchor waiting for its return date.
    if (!endDate) {
      if (ds > startDate && isRangeAvailable(startDate, ds)) setEndDate(ds);
      else if (isStartCandidate(ds)) setStartDate(ds);
      return;
    }
    if (ds === startDate || ds === endDate) return;
    // LEFT of the range → move the START handle (or re-anchor if the span breaks).
    if (ds < startDate) {
      if (isStartCandidate(ds) && isRangeAvailable(ds, endDate)) setStartDate(ds);
      else if (isStartCandidate(ds)) { setStartDate(ds); setEndDate(''); }
      return;
    }
    // RIGHT of the range → move the RETURN handle (or re-anchor on a valid start).
    if (ds > endDate) {
      if (isRangeAvailable(startDate, ds)) setEndDate(ds);
      else if (isStartCandidate(ds)) { setStartDate(ds); setEndDate(''); }
      return;
    }
    // Inside the range → shrink by moving the NEAREST edge.
    const dStart = dayjs(ds).diff(dayjs(startDate), 'day');
    const dEnd = dayjs(endDate).diff(dayjs(ds), 'day');
    if (dStart <= dEnd && isRangeAvailable(ds, endDate)) setStartDate(ds);
    else if (isRangeAvailable(startDate, ds)) setEndDate(ds);
  };

  // Paging back is always allowed (e.g. to review the booking's original month);
  // past days simply aren't selectable unless the override is on.
  const prevMonthDisabled = false;

  // Recompute full totals from a rental subtotal, mirroring checkout math so
  // the displayed figures match exactly what gets persisted.
  const computeTotalsFromSubtotal = (subtotal: number) => {
    // Mirror checkout: a percentage promo code scales with the subtotal, so a
    // rescheduled (cheaper/pricier) booking keeps the same discount RATE the
    // customer checked out with. Fall back to the stored amount when the code
    // isn't loaded/found (manual discounts, deleted codes).
    let discount = parseFloat(details?.discount_amount) || 0;
    if (details?.promo_code) {
      const dc: any = discountCodes.find((c: any) => c.id == details.promo_code);
      if (dc) {
        discount = Number(dc.type) === 1
          ? Math.round(subtotal * parseFloat(dc.amount)) / 100
          : parseFloat(dc.amount) || 0;
      }
    }
    const store = details?.store || {};
    const driverTip = Number(details?.driver_tip) || 0;
    const deliveryCharge = Number(details?.delivery_charge) || 0;
    let service_fee = 0;
    if (store.is_service_fee_enabled && store.service_fee_percentage) {
      // Match the customer vehicle checkout (VehicleReservationProvider): the fee
      // is charged on the DISCOUNTED subtotal, so a 100% discount code means $0
      // fee — not a fee on the pre-discount price the customer never paid.
      service_fee = Math.round(Math.max(0, subtotal - discount) * (parseFloat(store.service_fee_percentage) / 100) * 100) / 100;
    }
    let bodhi = 0;
    if (store.is_bodhi_protect_enabled && !details?.bodhi_protect_opted_out && store.bodhi_protect_percentage) {
      bodhi = Math.round(subtotal * (parseFloat(store.bodhi_protect_percentage) / 100) * 100) / 100;
    }
    const taxRate = details?.tax_rate ? details.tax_rate / 100 : 0;
    const tax = (subtotal - discount + driverTip + deliveryCharge + service_fee) * taxRate || 0;
    const total = subtotal - discount + driverTip + deliveryCharge + service_fee + tax + bodhi;
    return { subtotal, discount, service_fee, bodhi, tax, total };
  };

  // The active selection is either the [startDate, endDate] range picked on the
  // two calendars, OR (when hourlyDur is set) a same-day hourly rental whose
  // return time is start_time + N hours.
  const newEndTime = useMemo(() => {
    if (hourlyDur) {
      const hours = Number(hourlyDur.value) || 0;
      return dayjs(`2000-01-01 ${startTime24}`, 'YYYY-MM-DD HH:mm:ss')
        .add(hours, 'hour')
        .format('HH:mm:ss');
    }
    return origEndTime || toHHMMSS(dailyReturnTime);
  }, [hourlyDur, startTime24, origEndTime, dailyReturnTime]);
  const displayEndDate = hourlyDur ? startDate : endDate;
  const effectiveDays = useMemo(() => {
    const d = dayjs(endDate).startOf('day').diff(dayjs(startDate).startOf('day'), 'day');
    return d > 0 ? d : 1;
  }, [startDate, endDate]);
  const rangeAvailable = useMemo(
    () => isRangeAvailable(startDate, endDate),
    [startDate, endDate, datesByFamily, familiesSig, adminOverride] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const rangeSubtotal = useMemo(
    () => priceForDays(effectiveDays),
    [effectiveDays, startDate, datesByFamily, familiesSig, useExtraDay] // eslint-disable-line react-hooks/exhaustive-deps
  );
  // Hourly option resolved against per-family durations at the selected start date.
  const hourlyOption = useMemo(() => {
    if (!hourlyDur || families.length === 0 || !startDate) return null;
    let subtotal = 0;
    let availableAll = true;
    let label = `${hourlyDur.value} Hour(s)`;
    families.forEach((f) => {
      const e = (datesByFamily[f.family_id] || {})[startDate];
      const rec = rentalDurs(e).find((d: any) => durKey(d) === hourlyDur.id);
      if (!rec || rec.is_available === false) availableAll = false;
      if (rec) {
        subtotal += (parseFloat(rec.price) || 0) * f.quantity;
        label = rec.duration_label || label;
      }
    });
    return {
      id: `hourly-${hourlyDur.id}`,
      type: 'hourly',
      days: 0,
      hours: hourlyDur.value,
      endDate: startDate,
      label,
      subtotal: Math.round(subtotal * 100) / 100,
      is_available: (availableAll && (availableSet.has(startDate) || startDate === initialStart)) || adminOverride,
    };
  }, [hourlyDur, datesByFamily, familiesSig, startDate, availableSet, initialStart, adminOverride]); // eslint-disable-line react-hooks/exhaustive-deps

  const effectiveOption = useMemo(
    () => {
      if (hourlyOption) return hourlyOption;
      return startDate && endDate && endDate > startDate
        ? {
            id: 'range',
            type: 'range',
            days: effectiveDays,
            hours: 0,
            endDate,
            label: `${effectiveDays} Day${effectiveDays > 1 ? 's' : ''}`,
            subtotal: rangeSubtotal,
            is_available: rangeAvailable && (availableSet.has(startDate) || startDate === initialStart),
          }
        : null;
    },
    [hourlyOption, startDate, endDate, effectiveDays, rangeSubtotal, rangeAvailable, availableSet, initialStart]
  );
  // A real reschedule = new pickup date OR new return date OR hourly mode vs the current booking.
  const hasChange = startDate !== initialStart || endDate !== origEndDate || !!hourlyDur;

  const handleApply = () => {
    if (!isStartCandidate(startDate)) {
      showAlert('error', 'Please pick an available start date.');
      return;
    }
    const opt = effectiveOption;
    if (!opt || !opt.is_available) {
      showAlert('error', `The vehicle isn't available for ${effectiveOption?.label || 'this duration'} from this start date.`);
      return;
    }
    const end_date = opt.endDate;
    const end_time = newEndTime;
    const start_time = startTime24;

    const newItems = items.map((it: any) => {
      let unit = 0;
      let price_id: any = null;
      if (opt.type === 'hourly' && hourlyDur) {
        // Hourly mode: read the per-family price for the selected hourly tier
        // directly from the availability payload — day-tier math doesn't apply.
        const e = (datesByFamily[it.family_id] || {})[startDate];
        const rec = rentalDurs(e).find((d: any) => durKey(d) === hourlyDur.id);
        unit = parseFloat(rec?.price) || 0;
        price_id = rec?.price_id ?? null;
      } else {
        // Per-day rate from the nearest tier for the selected range's day-count.
        const p = itemPricing(it.family_id, opt.days);
        unit = p.unit;
        price_id = p.price_id;
      }
      const unitPrice = unit || parseFloat(it.price) || 0;
      const qty = it.quantity || 1;
      return {
        id: it.id,
        family_id: it.family_id,
        display_name: it.display_name,
        price_group_id: it.price_group_id,
        quantity: qty,
        price: unitPrice * qty,
        price_id: price_id ?? it.price_id,
        size: it.size,
        extras: it.extras || [],
      };
    });
    const { subtotal, discount, service_fee, bodhi, tax, total } = computeTotalsFromSubtotal(opt.subtotal);

    setApplying(true);
    updateReservation(
      {
        id: details.id,
        start_date: startDate,
        end_date,
        start_time,
        end_time,
        brand_id: details.brand_id,
        delivery_method: orderType === 'Delivery' ? 1 : 0,
        plantation_id: orderType === 'Delivery' ? (plantationId ?? null) : (details.plantation_id ?? null),
        ...(orderType === 'Delivery' && addrTouched
          ? {
              use_manual: useManual,
              address_id: useManual ? null : (addressId ?? null),
              manual_address: useManual ? (manualAddress || null) : null,
              manually_updated: true,
              is_address_synced: false,
            }
          : {}),
        items: newItems,
        subtotal,
        tax_amount: tax,
        discount_amount: discount,
        service_fee,
        bodhi_protect_price: bodhi,
        total_price: total,
      },
      (jsonRes: any, status: any) => {
        setApplying(false);
        if (status === 201) {
          showAlert('success', 'Reservation schedule updated.');
          setUpdateCount((prev) => prev + 1);
          setModalVisible(false);
        } else {
          showAlert('error', jsonRes?.error || 'Could not update schedule.');
        }
      }
    );
  };

  return (
    <View style={{ marginBottom: 10, zIndex: 30 }}>
      <View style={{ flexDirection: 'row', alignItems: 'flex-end', marginTop: 10, marginBottom: 12 }}>
        <View style={{ marginRight: 16 }}>
          <Text style={{ color: '#555555', fontSize: 12, marginBottom: 4 }}>{'Start date'}</Text>
          <TextInput
            value={`${fmtDateMDY(initialStart)} ${formatTime12(startTime24)}`}
            editable={false}
            style={{ minWidth: 170, height: 38, borderWidth: 1, borderColor: '#cccccc', borderRadius: 4, paddingHorizontal: 10, color: '#333333', backgroundColor: '#f2f2f2' }}
          />
        </View>
        <View style={{ marginRight: 16 }}>
          <Text style={{ color: '#555555', fontSize: 12, marginBottom: 4 }}>{'End date'}</Text>
          <TextInput
            value={`${fmtDateMDY(origEndDate)} ${formatTime12(origEndTime)}`}
            editable={false}
            style={{ minWidth: 170, height: 38, borderWidth: 1, borderColor: '#cccccc', borderRadius: 4, paddingHorizontal: 10, color: '#333333', backgroundColor: '#f2f2f2' }}
          />
        </View>
        <TouchableOpacity onPress={() => setModalVisible(true)} style={{ paddingBottom: 9 }}>
          <Text style={{ color: '#1a8a8a', fontWeight: '600', textDecorationLine: 'underline' }}>{'Reschedule / change dates'}</Text>
        </TouchableOpacity>
      </View>

      <Modal visible={modalVisible} transparent animationType="fade" onRequestClose={() => setModalVisible(false)}>
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'center', alignItems: 'center', padding: 12 }}>
          <View style={{ width: '100%', maxWidth: 1400, maxHeight: '96%', backgroundColor: '#ffffff', borderRadius: 12, overflow: 'hidden' }}>
            <View style={{ paddingHorizontal: 16, paddingVertical: 14, borderBottomWidth: 1, borderColor: '#eeeeee' }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                <Text numberOfLines={1} style={{ flex: 1, textAlign: 'center', fontSize: 16, fontWeight: '700', color: '#333333' }}>
                  {items.map((it: any) => it.display_name).filter(Boolean).join(', ') || 'Reservation'}
                </Text>
                <TouchableOpacity onPress={() => setModalVisible(false)} style={{ position: 'absolute', right: 0, paddingHorizontal: 6, paddingVertical: 2 }}>
                  <Text style={{ fontSize: 26, color: '#777777', lineHeight: 26 }}>{'×'}</Text>
                </TouchableOpacity>
              </View>
            </View>
            <ScrollView contentContainerStyle={{ padding: 16 }}>
              <Text style={{ marginBottom: 12, color: '#333333', fontSize: 15, fontWeight: '700' }}>{'Reschedule (vehicle availability)'}</Text>
              <Text style={{ marginBottom: 10, color: '#777777', fontSize: 13 }}>
                {`Currently booked: ${fmtDateMDY(initialStart)} ${formatTime12(startTime24)} → ${fmtDateMDY(origEndDate)} ${formatTime12(origEndTime)}  ·  $${originalTotal.toFixed(2)}`}
              </Text>

              <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: adminOverride ? '#fff6e5' : '#f7f7f7', borderWidth: 1, borderColor: adminOverride ? '#f0c36d' : '#e6e6e6', borderRadius: 8, paddingVertical: 8, paddingHorizontal: 12, marginBottom: 12 }}>
                <Switch value={adminOverride} onValueChange={setAdminOverride} />
                <View style={{ marginLeft: 10, flex: 1 }}>
                  <Text style={{ color: '#333333', fontSize: 13, fontWeight: '600' }}>{'Override date restrictions'}</Text>
                  <Text style={{ color: '#888888', fontSize: 11, marginTop: 1 }}>{'Allow past dates, cut-off and blockout days. Inventory availability still applies.'}</Text>
                </View>
              </View>

              <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 4 }}>
                <View style={{ marginRight: 16, marginBottom: 12 }}>
                  <Text style={{ color: '#555555', fontSize: 12, marginBottom: 4 }}>{'Start date & time'}</Text>
                  {/* Read-only field rendered as nested Texts so the TIME can be
                      marked red + underlined (TextInput can't style substrings). */}
                  <View style={{ width: 180, height: 38, borderWidth: 1, borderColor: '#cccccc', borderRadius: 4, paddingHorizontal: 10, backgroundColor: '#f2f2f2', justifyContent: 'center' }}>
                    <Text style={{ color: '#333333', fontSize: 14 }}>
                      {`${fmtDateMDY(startDate)} `}
                      <Text style={{ color: '#c0392b', fontWeight: '700', textDecorationLine: 'underline' }}>{formatTime12(startTime24)}</Text>
                    </Text>
                  </View>
                </View>
                <View style={{ marginRight: 16, marginBottom: 12 }}>
                  <Text style={{ color: '#555555', fontSize: 12, marginBottom: 4 }}>{'Return date & time'}</Text>
                  <View style={{ width: 180, height: 38, borderWidth: 1, borderColor: '#cccccc', borderRadius: 4, paddingHorizontal: 10, backgroundColor: '#f2f2f2', justifyContent: 'center' }}>
                    <Text style={{ color: '#333333', fontSize: 14 }}>
                      {`${fmtDateMDY(displayEndDate)} `}
                      <Text style={{ color: '#c0392b', fontWeight: '700', textDecorationLine: 'underline' }}>{formatTime12(newEndTime)}</Text>
                    </Text>
                  </View>
                </View>
                <View style={{ marginRight: 16, marginBottom: 12 }}>
                  <Text style={{ color: '#555555', fontSize: 12, marginBottom: 4 }}>{'Order type'}</Text>
                  <View style={{ borderWidth: 1, borderColor: '#cccccc', borderRadius: 4, width: 160, backgroundColor: '#ffffff', justifyContent: 'center' }}>
                    <Picker
                      selectedValue={orderType}
                      onValueChange={(v) => { setOrderType(String(v)); setOrderTypeTouched(true); }}
                      style={{ height: 38, paddingHorizontal: 10, color: '#333333', borderWidth: 0, backgroundColor: 'transparent' }}
                    >
                      <Picker.Item label="Pickup" value="Pickup" />
                      <Picker.Item label="Delivery" value="Delivery" />
                    </Picker>
                  </View>
                </View>
                {!SHOW_DELIVERY_ADDRESS_FIELDS && orderType === 'Delivery' && (
                  <View style={{ marginRight: 16, marginBottom: 12 }}>
                    <Text style={{ color: '#555555', fontSize: 12, marginBottom: 4 }}>{'Delivery area'}</Text>
                    <View style={{ borderWidth: 1, borderColor: missingDeliveryArea ? '#c0392b' : '#cccccc', borderRadius: 4, width: 360, backgroundColor: '#ffffff', justifyContent: 'center' }}>
                      <Picker
                        selectedValue={plantationId ?? ''}
                        onValueChange={(v) => setPlantationId(v === '' ? null : Number(v))}
                        style={{ height: 38, paddingHorizontal: 10, color: '#333333', borderWidth: 0, backgroundColor: 'transparent' }}
                      >
                        <Picker.Item label="Select area…" value="" />
                        {plantations.map((p: any) => (
                          <Picker.Item key={p.id} label={areaLabel(p)} value={p.id} />
                        ))}
                      </Picker>
                    </View>
                  </View>
                )}
                {!loading && dayAvailable && effectiveOption && effectiveOption.is_available && hasChange && (
                  <TouchableOpacity
                    disabled={applying}
                    onPress={handleApply}
                    style={{ backgroundColor: applying ? '#9bbbbb' : '#1a8a8a', paddingVertical: 10, paddingHorizontal: 20, borderRadius: 6, marginBottom: 12, height: 38, justifyContent: 'center' }}
                  >
                    <Text style={{ color: 'white', fontWeight: '600' }}>
                      {applying ? 'Updating…' : 'Update reservation'}
                    </Text>
                  </TouchableOpacity>
                )}
              </View>

              {missingDeliveryArea && !SHOW_DELIVERY_ADDRESS_FIELDS && (
                <Text style={{ color: '#c0392b', fontSize: 12, marginBottom: 8, maxWidth: 640 }}>
                  {'This delivery order has no area on file — prices below EXCLUDE the area delivery charge. Select the customer\u2019s area to price correctly.'}
                </Text>
              )}

              {SHOW_DELIVERY_ADDRESS_FIELDS && orderType === 'Delivery' && (
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: 8, columnGap: 16 }}>
                  <View style={{ marginBottom: 12, width: 220 }}>
                    <Text style={{ color: '#555555', fontSize: 12, marginBottom: 4, height: 16, lineHeight: 16 }}>{'Delivery area'}</Text>
                    <View style={{ borderWidth: 1, borderColor: '#cccccc', borderRadius: 4, width: '100%', backgroundColor: '#ffffff', justifyContent: 'center' }}>
                      <Picker
                        selectedValue={plantationId ?? ''}
                        onValueChange={(v) => setPlantationId(v === '' ? null : Number(v))}
                        style={{ height: 38, paddingHorizontal: 10, color: '#333333', borderWidth: 0, backgroundColor: 'transparent' }}
                      >
                        <Picker.Item label="Select area…" value="" />
                        {plantations.map((p: any) => (
                          <Picker.Item key={p.id} label={areaLabel(p)} value={p.id} />
                        ))}
                      </Picker>
                    </View>
                  </View>
                  <View style={{ marginBottom: 12, flexGrow: 1, flexBasis: 280, minWidth: 260, maxWidth: 460 }}>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, height: 16 }}>
                      <Text style={{ color: '#555555', fontSize: 12, lineHeight: 16 }}>{useManual ? 'Delivery address (manual)' : 'Delivery address'}</Text>
                      <TouchableOpacity
                        onPress={() => {
                          const toManual = !useManual;
                          setUseManual(toManual);
                          setAddrTouched(true);
                          if (toManual) { setSelectedAddress(null); setAddressId(null); setSearchedAddresses([]); }
                          else { setManualAddress(''); setSearchKey(''); }
                        }}
                      >
                        <Text style={{ color: '#1a8a8a', fontSize: 12, lineHeight: 16 }}>{useManual ? 'Search instead' : 'Enter manually'}</Text>
                      </TouchableOpacity>
                    </View>
                    {Platform.OS === 'web' && !useManual ? (
                      <Autocomplete
                        freeSolo
                        disableClearable
                        sx={{ width: '100%' }}
                        slotProps={{ popper: { sx: { zIndex: 2147483000 } } }}
                        options={searchedAddresses}
                        getOptionLabel={(opt: any) => (typeof opt === 'string' ? opt : String(opt?.label || '').replace(/<[^>]*>?/gm, ''))}
                        value={selectedAddress}
                        inputValue={searchKey}
                        onChange={onPickAddress}
                        onInputChange={(_event, value) => {
                          setSearchKey(value);
                          if (searchTimer.current) clearTimeout(searchTimer.current);
                          searchTimer.current = setTimeout(() => { getAddresses(value); }, 500);
                        }}
                        filterOptions={(x) => x}
                        renderInput={(params) => {
                          const text = String(params?.inputProps?.value ?? ' ');
                          const spanIndex = text.indexOf('<span');
                          const inputVal = spanIndex !== -1 ? text.substring(0, spanIndex) : text;
                          return (
                            <TextField
                              {...params}
                              placeholder="Search address…"
                              inputProps={{ ...params.inputProps, type: 'search', value: inputVal, style: { padding: '6px', fontSize: '13px' } }}
                            />
                          );
                        }}
                        renderOption={(props, option: any) => (
                          <li {...props}><span style={{ fontSize: '13px' }} dangerouslySetInnerHTML={{ __html: option.label }} /></li>
                        )}
                      />
                    ) : Platform.OS === 'web' ? (
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        placeholder="Resort, Street Address, Apt #/Suite/Etc."
                        value={manualAddress}
                        onChange={(e: any) => { setManualAddress(e.target.value); setAddrTouched(true); }}
                        inputProps={{ style: { fontSize: '13px' } }}
                      />
                    ) : (
                      <TextInput
                        value={useManual ? manualAddress : deliveryAddress}
                        onChangeText={(v) => { setManualAddress(v); setUseManual(true); setAddrTouched(true); }}
                        placeholder="Delivery address"
                        style={{ minHeight: 38, borderWidth: 1, borderColor: '#cccccc', borderRadius: 4, paddingHorizontal: 10, color: '#333333' }}
                      />
                    )}
                  </View>
                </View>
              )}

              <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                <View style={{ marginRight: 24, marginBottom: 12 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
                    <Text style={{ fontSize: 16, fontWeight: '800', color: !endDate ? '#c77700' : '#1a8a8a', marginRight: 14 }}>
                      {!endDate
                        ? `Start ${fmtDateMDY(startDate)} picked — now tap the RETURN date`
                        : 'Tap before the range to move START · after it to move RETURN'}
                    </Text>
                    <View style={{ flexDirection: 'row', alignItems: 'center', marginRight: 10 }}>
                      <View style={{ width: 12, height: 12, borderRadius: 6, backgroundColor: '#1a8a8a', marginRight: 4 }} />
                      <Text style={{ fontSize: 11, color: '#777777' }}>{'start / return'}</Text>
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <View style={{ width: 12, height: 12, backgroundColor: '#cdeeee', marginRight: 4 }} />
                      <Text style={{ fontSize: 11, color: '#777777' }}>{'selected range'}</Text>
                    </View>
                  </View>
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', columnGap: 12, rowGap: 12 }}>
                    <CalendarGrid
                      viewMonth={viewMonth}
                      prevDisabled={prevMonthDisabled}
                      showNext={false}
                      onPrev={() => setViewMonth((m) => m.subtract(1, 'month'))}
                      onNext={() => {}}
                      startDate={startDate}
                      endDate={hourlyDur ? startDate : endDate}
                      isSelectable={isDateSelectable}
                      isUnavailable={isDateUnavailable}
                      onPick={onPickDate}
                      width={300}
                    />
                    <CalendarGrid
                      viewMonth={rightMonth}
                      showPrev={false}
                      onPrev={() => {}}
                      onNext={() => setViewMonth((m) => m.add(1, 'month'))}
                      startDate={startDate}
                      endDate={hourlyDur ? startDate : endDate}
                      isSelectable={isDateSelectable}
                      isUnavailable={isDateUnavailable}
                      onPick={onPickDate}
                      width={300}
                    />
                    {/* Third grid: always-visible list of the bookings that grey out days
                        in the two visible months, with order numbers and exact ranges. */}
                    <View style={{ width: 320, borderWidth: 1, borderColor: '#e0e0e0', borderRadius: 8, backgroundColor: '#ffffff', padding: 12, alignSelf: 'stretch' }}>
                      <Text style={{ fontSize: 13, fontWeight: '700', color: '#333333', marginBottom: 8 }}>
                        {`Availability · ${viewMonth.format('MMM')} – ${rightMonth.format('MMM YYYY')}`}
                      </Text>
                      <ScrollView style={{ maxHeight: 340 }}>
                        <View style={{ marginBottom: 10, borderWidth: 1, borderColor: '#dbeaea', borderRadius: 6, backgroundColor: '#f4fafa', paddingVertical: 6, paddingHorizontal: 8 }}>
                          <Text style={{ fontSize: 11, color: '#333333' }}>
                            <Text style={{ fontWeight: '700' }}>{'Brand: '}</Text>
                            {brandName || `#${details?.brand_id ?? ''}`}
                          </Text>
                          {families.map((f) => (
                            <Text key={f.family_id} style={{ fontSize: 11, color: '#333333', marginTop: 2 }}>
                              {`${familyName.get(f.family_id) || `item #${f.family_id}`} — `}
                              <Text style={{ fontWeight: '700', color: '#1a8a8a' }}>
                                {locationQty[f.family_id] == null ? '…' : `${locationQty[f.family_id]} unit${locationQty[f.family_id] === 1 ? '' : 's'}`}
                              </Text>
                              {" at this brand's location"}
                            </Text>
                          ))}
                        </View>
                        <Text style={{ fontSize: 12, fontWeight: '700', color: '#2e7d32', marginBottom: 4 }}>{'✓ Available to book'}</Text>
                        {availability.open.length > 0 ? (
                          <View style={{ marginBottom: 10, borderWidth: 1, borderColor: '#d7ecd9', borderRadius: 6, backgroundColor: '#f3faf3', paddingVertical: 4, paddingHorizontal: 8 }}>
                            {availability.open.map((g, i) => (
                              <Text key={i} style={{ fontSize: 11, fontWeight: '700', color: '#2e7d32', paddingVertical: 3 }}>
                                {g.from === g.to ? fmtDateNice(g.from) : `${fmtDateNice(g.from)} – ${fmtDateNice(g.to)}`}
                                {adminOverride && dayjs(g.to).isBefore(dayjs(), 'day') ? (
                                  <Text style={{ fontWeight: '400', color: '#8a6d1a' }}>{'  (past — override on)'}</Text>
                                ) : null}
                              </Text>
                            ))}
                          </View>
                        ) : (
                          <Text style={{ fontSize: 11, color: '#888888', marginBottom: 10 }}>
                            {loading ? 'Checking…' : 'Nothing bookable in these two months.'}
                          </Text>
                        )}
                        <Text style={{ fontSize: 12, fontWeight: '700', color: '#c0392b', marginBottom: 4 }}>{'✗ Not available'}</Text>
                        {availability.closed.length > 0 ? (
                          <View style={{ marginBottom: 10, borderWidth: 1, borderColor: '#f5d9d7', borderRadius: 6, backgroundColor: '#fdf5f4', paddingVertical: 4, paddingHorizontal: 8 }}>
                            {availability.closed.map((g, i) => (
                              <View key={i} style={{ paddingVertical: 3, borderTopWidth: i === 0 ? 0 : 1, borderColor: '#f5d9d7' }}>
                                <Text style={{ fontSize: 11, fontWeight: '700', color: '#333333' }}>
                                  {g.from === g.to ? fmtDateNice(g.from) : `${fmtDateNice(g.from)} – ${fmtDateNice(g.to)}`}
                                </Text>
                                <Text style={{ fontSize: 11, color: '#a94442', marginTop: 1 }}>{g.reason}</Text>
                              </View>
                            ))}
                          </View>
                        ) : (
                          !loading && <Text style={{ fontSize: 11, color: '#888888', marginBottom: 10 }}>{'Every date in these two months is available.'}</Text>
                        )}
                        <Text style={{ fontSize: 12, fontWeight: '700', color: '#333333', marginBottom: 2 }}>{'Bookings'}</Text>
                        <Text style={{ fontSize: 10, color: '#999999', marginBottom: 6 }}>
                          {'Orders occupying this vehicle in these two months.'}
                        </Text>
                        {visibleBookings.length === 0 ? (
                          <Text style={{ fontSize: 12, color: '#888888' }}>
                            {loading ? 'Loading bookings…' : 'No bookings in these months.'}
                          </Text>
                        ) : (
                          visibleBookings.map((b: any) => (
                            <View key={b.orderNumber} style={{ borderWidth: 1, borderColor: b.isCurrent ? '#1a8a8a' : '#eeeeee', borderRadius: 6, padding: 8, marginBottom: 6, backgroundColor: b.isCurrent ? '#f0f9f9' : '#fafafa' }}>
                              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                <Text style={{ fontSize: 12, fontWeight: '700', color: '#1a8a8a', flex: 1 }}>{`#${b.orderNumber}`}</Text>
                                {b.isCurrent && (
                                  <View style={{ backgroundColor: '#1a8a8a', borderRadius: 8, paddingHorizontal: 7, paddingVertical: 1, marginRight: 4 }}>
                                    <Text style={{ fontSize: 10, fontWeight: '700', color: '#ffffff' }}>{'this order'}</Text>
                                  </View>
                                )}
                                <View style={{ backgroundColor: '#e6f4f4', borderRadius: 8, paddingHorizontal: 7, paddingVertical: 1 }}>
                                  <Text style={{ fontSize: 11, fontWeight: '700', color: '#1a8a8a' }}>{`Qty ${b.qty}`}</Text>
                                </View>
                              </View>
                              <Text style={{ fontSize: 11, color: '#777777', marginTop: 3 }} numberOfLines={1}>{b.item}</Text>
                              <Text style={{ fontSize: 11, color: '#555555', marginTop: 2 }}>
                                {fmtDateNice(b.from)}
                                {b.fromTime ? (
                                  <Text style={{ color: '#c0392b', fontWeight: '700', textDecorationLine: 'underline' }}>{` ${formatTime12(b.fromTime)}`}</Text>
                                ) : null}
                                {' → '}
                                {fmtDateNice(b.to)}
                                {b.toTime ? (
                                  <Text style={{ color: '#c0392b', fontWeight: '700', textDecorationLine: 'underline' }}>{` ${formatTime12(b.toTime)}`}</Text>
                                ) : null}
                              </Text>
                            </View>
                          ))
                        )}
                      </ScrollView>
                    </View>
                  </View>
                  {loading && (
                    <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 8 }}>
                      <ActivityIndicator size="small" color="#1a8a8a" />
                      <Text style={{ fontSize: 12, color: '#1a8a8a', marginLeft: 8, fontWeight: '600' }}>{'Loading availability…'}</Text>
                    </View>
                  )}
                  <Text style={{ maxWidth: 950, fontSize: 11, color: '#999999', marginTop: 8, lineHeight: 15 }}>
                    {adminOverride
                      ? 'Struck-through dates have no inventory (fully booked) — see the bookings panel for the blocking orders. Date restrictions are overridden.'
                      : 'Struck-through dates can\u2019t be booked (or can\u2019t complete the selected range): already passed, past the cut-off, within a blockout, or taken by the bookings listed on the right. Any consecutive available range works — even across the two months.'}
                  </Text>
                </View>

                <View style={{ flex: 1, minWidth: 300 }}>
      {!loading && (
        <View style={{ marginBottom: 10 }}>
          {endDate ? (
            <>
              <Text style={{ color: '#1a8a8a', fontSize: 15, fontWeight: '700' }}>
                {`End date: ${fmtDateMDY(displayEndDate)} ${formatTime12(newEndTime)}`}
              </Text>
              <Text style={{ color: '#888888', fontSize: 12, marginTop: 2 }}>
                {`${fmtDateMDY(startDate)} ${formatTime12(startTime24)} → ${fmtDateMDY(displayEndDate)} ${formatTime12(newEndTime)}  ·  ${effectiveOption ? effectiveOption.label : `${effectiveDays} day${effectiveDays > 1 ? 's' : ''}`}${hasChange ? '' : ' (current)'}`}
              </Text>
            </>
          ) : (
            <Text style={{ color: '#1a8a8a', fontSize: 15, fontWeight: '700' }}>
              {`Start: ${fmtDateMDY(startDate)} — now pick a return date (or tap a preset below)`}
            </Text>
          )}
        </View>
      )}

      <Text style={{ marginBottom: 6, color: '#888888', fontSize: 12 }}>{`Current booking is ${originalDays} day${originalDays > 1 ? 's' : ''} (${fmtDateMDY(initialStart)} → ${fmtDateMDY(origEndDate)})`}</Text>
      {!loading && extraDayRate > 0 && (
        <TouchableOpacity
          activeOpacity={0.7}
          onPress={() => setUseExtraDay((v) => !v)}
          style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}
        >
          <View style={{ width: 16, height: 16, borderRadius: 3, borderWidth: 1.5, borderColor: '#1a8a8a', backgroundColor: useExtraDay ? '#1a8a8a' : '#ffffff', alignItems: 'center', justifyContent: 'center', marginRight: 7 }}>
            {useExtraDay && <Text style={{ color: '#ffffff', fontSize: 11, fontWeight: '700', lineHeight: 13 }}>{'✓'}</Text>}
          </View>
          <Text style={{ fontSize: 12, color: '#333333' }}>
            {`Extra day rate: `}
            <Text style={{ fontWeight: '700', color: '#1a8a8a' }}>{`$${extraDayRate.toFixed(2)}/day`}</Text>
            {` for each additional day beyond the listed tiers`}
          </Text>
        </TouchableOpacity>
      )}
      {!loading && dayAvailable && (
        <View>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', maxWidth: 460 }}>
            {dayOptions.map((opt) => {
              const { id, label, subtotal, is_available } = opt;
              const isHourly = opt.type === 'hourly';
              const disabled = !is_available;
              // Chips are quick presets. Day/week/month tiers set the return date
              // to start + the tier's length. Hourly tiers switch the editor into
              // hourly mode (same-day; return time = start + N hours).
              const active = isHourly
                ? !!hourlyDur && hourlyDur.id === id
                : !hourlyDur && (opt.endDate === endDate || (!!rangeBreakdown && opt.days === rangeBreakdown.baseDays));
              return (
                <TouchableOpacity
                  key={id}
                  disabled={disabled}
                  activeOpacity={0.7}
                  onPress={() => {
                    if (isHourly) {
                      setHourlyDur({ id, value: Number(opt.value) });
                    } else {
                      setHourlyDur(null);
                      setEndDate(opt.endDate);
                    }
                  }}
                  style={{
                    paddingVertical: 5,
                    paddingHorizontal: 9,
                    marginRight: 6,
                    marginBottom: 6,
                    borderWidth: 1,
                    borderStyle: isHourly ? 'dashed' : 'solid',
                    borderColor: active ? '#1a8a8a' : isHourly ? '#8a6d3b' : '#cccccc',
                    backgroundColor: disabled ? '#f2f2f2' : active ? '#e6f4f4' : isHourly ? '#fdf7e6' : '#ffffff',
                    borderRadius: 12,
                    alignItems: 'center',
                    minWidth: 64,
                    opacity: disabled ? 0.6 : 1,
                  }}
                >
                  <Text style={{ fontSize: 12, color: disabled ? '#999999' : active ? '#1a8a8a' : isHourly ? '#8a6d3b' : '#333333', fontWeight: active ? '700' : '500' }}>
                    {label}
                  </Text>
                  <Text style={{ color: disabled ? '#999999' : active ? '#1a8a8a' : isHourly ? '#8a6d3b' : '#555555', fontSize: 10, marginTop: 1 }}>{`$${(subtotal || 0).toFixed(2)}`}</Text>
                  {disabled ? (
                    <Text style={{ color: '#B8393C', fontSize: 9, marginTop: 1 }}>{'Unavailable'}</Text>
                  ) : isHourly ? (
                    <Text style={{ color: '#8a6d3b', fontSize: 9, marginTop: 1 }}>{'Hourly'}</Text>
                  ) : null}
                </TouchableOpacity>
              );
            })}
            {rangeBreakdown && (
              <View
                style={{
                  paddingVertical: 6,
                  paddingHorizontal: 11,
                  marginRight: 6,
                  marginBottom: 6,
                  borderWidth: 1.5,
                  borderStyle: 'dashed',
                  borderColor: '#c77700',
                  backgroundColor: '#fff6e5',
                  borderRadius: 12,
                  alignItems: 'center',
                  minWidth: 84,
                }}
              >
                <Text style={{ fontSize: 13, color: '#c77700', fontWeight: '700' }}>
                  {`+${rangeBreakdown.extraDays} Extra Day${rangeBreakdown.extraDays > 1 ? 's' : ''}`}
                </Text>
                <Text style={{ color: '#c77700', fontSize: 11, fontWeight: '700', marginTop: 1 }}>{`+$${rangeBreakdown.extraAmount.toFixed(2)}`}</Text>
              </View>
            )}
          </View>
        </View>
      )}

      {loading && (
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: 12,
            paddingVertical: 24,
            borderWidth: 1,
            borderColor: '#e0e0e0',
            borderRadius: 8,
            backgroundColor: '#fafafa',
          }}
        >
          <ActivityIndicator size="small" color="#1a8a8a" />
          <Text style={{ marginLeft: 10, color: '#1a8a8a', fontSize: 13, fontWeight: '600' }}>{'Checking availability & pricing…'}</Text>
        </View>
      )}

      {!loading && availableStartDates.length === 0 && (
        <Text style={{ color: '#B8393C', marginTop: 8, fontSize: 13 }}>
          {'No available start dates / daily pricing for this vehicle in the next 90 days.'}
        </Text>
      )}

      {!loading && dayAvailable && effectiveOption && (() => {
        const opt = effectiveOption;
        if (!opt.is_available) {
          // If the start day itself is blocked, explain that directly; otherwise show
          // the rental-span conflict as a table of the orders holding the inventory.
          const startReason = explainDate(startDate);
          const conflict = startReason ? null : getRentalConflict(startDate, opt.endDate);
          return (
            <View style={{ marginTop: 10, backgroundColor: '#fdecea', borderRadius: 8, padding: 12 }}>
              <Text style={{ color: '#B8393C', fontSize: 13, fontWeight: '700' }}>{'This date is unavailable'}</Text>
              {startReason ? (
                <Text style={{ color: '#B8393C', fontSize: 13, marginTop: 4, lineHeight: 18 }}>
                  {startReason + nextActionHint(startDate)}
                </Text>
              ) : conflict ? (
                <View style={{ marginTop: 4 }}>
                  <Text style={{ color: '#B8393C', fontSize: 13, lineHeight: 18 }}>
                    {`A ${opt.label} rental starting ${fmtDateMDY(startDate)} can't be booked — ${conflict.itemName} is already reserved on one or more of those days` + (conflict.orders.length > 0 ? ' by:' : '.')}
                  </Text>
                  {conflict.orders.length > 0 && (
                    <View style={{ marginTop: 8, borderWidth: 1, borderColor: '#f0b3b0', borderRadius: 6, overflow: 'hidden' }}>
                      <View style={{ flexDirection: 'row', backgroundColor: '#f7d3d1' }}>
                        <Text style={{ flex: 1.1, fontSize: 11, fontWeight: '700', color: '#8f2b2e', paddingVertical: 6, paddingHorizontal: 8 }}>{'Order #'}</Text>
                        <Text style={{ flex: 1.5, fontSize: 11, fontWeight: '700', color: '#8f2b2e', paddingVertical: 6, paddingHorizontal: 8 }}>{'Start'}</Text>
                        <Text style={{ flex: 1.5, fontSize: 11, fontWeight: '700', color: '#8f2b2e', paddingVertical: 6, paddingHorizontal: 8 }}>{'End'}</Text>
                      </View>
                      {conflict.orders.map((o, i) => (
                        <View
                          key={o.orderNumber}
                          style={{ flexDirection: 'row', backgroundColor: i % 2 === 0 ? '#ffffff' : '#fdf3f2', borderTopWidth: 1, borderTopColor: '#f5c6c4' }}
                        >
                          <Text style={{ flex: 1.1, fontSize: 11, color: '#333333', paddingVertical: 6, paddingHorizontal: 8, fontWeight: '600' }}>{o.orderNumber}</Text>
                          <Text style={{ flex: 1.5, fontSize: 11, color: '#333333', paddingVertical: 6, paddingHorizontal: 8 }}>{`${fmtDateMDY(o.from)}${o.fromTime ? `\n${formatTime12(o.fromTime)}` : ''}`}</Text>
                          <Text style={{ flex: 1.5, fontSize: 11, color: '#333333', paddingVertical: 6, paddingHorizontal: 8 }}>{`${fmtDateMDY(o.to)}${o.toTime ? `\n${formatTime12(o.toTime)}` : ''}`}</Text>
                        </View>
                      ))}
                    </View>
                  )}
                  <Text style={{ color: '#B8393C', fontSize: 13, marginTop: 8, lineHeight: 18 }}>
                    {nextActionHint(startDate).trim()}
                  </Text>
                </View>
              ) : (
                <Text style={{ color: '#B8393C', fontSize: 13, marginTop: 4, lineHeight: 18 }}>
                  {`${fmtDateMDY(startDate)} isn't available for a ${opt.label} rental.` + nextActionHint(startDate)}
                </Text>
              )}
            </View>
          );
        }
        const t = computeTotalsFromSubtotal(opt.subtotal);
        const a = t.total - originalTotal;
        const paid = Number(details?.paid) || 0;
        const remaining = t.total - paid;
        return (
          <View style={{ marginTop: 16 }}>
            <View style={{ backgroundColor: remaining > 0.005 ? '#fdecea' : '#e7f6ec', borderRadius: 8, paddingVertical: 20, paddingHorizontal: 18 }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: rangeBreakdown ? 3 : 10 }}>
                <Text style={{ color: '#555555', fontSize: 13 }}>{'Subtotal'}</Text>
                <Text style={{ color: '#333333', fontSize: 13 }}>{`$${t.subtotal.toFixed(2)}`}</Text>
              </View>
              {rangeBreakdown && (
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 10 }}>
                  <Text style={{ color: '#c77700', fontSize: 11, fontStyle: 'italic' }}>
                    {`${rangeBreakdown.baseDays} Day${rangeBreakdown.baseDays > 1 ? 's' : ''} tier + ${rangeBreakdown.extraDays} extra day${rangeBreakdown.extraDays > 1 ? 's' : ''}`}
                  </Text>
                  <Text style={{ color: '#c77700', fontSize: 11, fontStyle: 'italic' }}>
                    {`$${rangeBreakdown.baseSubtotal.toFixed(2)} + $${rangeBreakdown.extraAmount.toFixed(2)}`}
                  </Text>
                </View>
              )}
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 10 }}>
                <Text style={{ color: '#555555', fontSize: 13 }}>{'Service Fee'}</Text>
                <Text style={{ color: '#333333', fontSize: 13 }}>{`$${t.service_fee.toFixed(2)}`}</Text>
              </View>
              {t.bodhi > 0 && (
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 10 }}>
                  <Text style={{ color: '#555555', fontSize: 13 }}>{'Bodhi Protect'}</Text>
                  <Text style={{ color: '#333333', fontSize: 13 }}>{`$${t.bodhi.toFixed(2)}`}</Text>
                </View>
              )}
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 10 }}>
                <Text style={{ color: '#555555', fontSize: 13 }}>{'Discount'}</Text>
                <Text style={{ color: '#333333', fontSize: 13 }}>{`$${t.discount.toFixed(2)}`}</Text>
              </View>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                <Text style={{ color: '#555555', fontSize: 13 }}>{`Tax${details?.tax_rate ? ` (${details.tax_rate}%)` : ''}`}</Text>
                <Text style={{ color: '#333333', fontSize: 13 }}>{`$${t.tax.toFixed(2)}`}</Text>
              </View>
              <View style={{ height: 1, backgroundColor: '#e0e0e0', marginVertical: 14 }} />
              <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                <Text style={{ color: '#333333', fontSize: 15, fontWeight: '700' }}>{'Total'}</Text>
                <Text style={{ color: '#333333', fontSize: 15, fontWeight: '700' }}>{`$${t.total.toFixed(2)}`}</Text>
              </View>
            </View>
            <View style={{ marginTop: 14, paddingHorizontal: 4, marginBottom: 6 }}>
              {missingDeliveryArea ? (
                <Text style={{ color: '#c0392b', fontSize: 12, fontWeight: '600' }}>
                  {'Paid vs. new total hidden: select the delivery area above first — the prices shown exclude the area delivery charge.'}
                </Text>
              ) : (
                <>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
                    <Text style={{ color: '#555555', fontSize: 13 }}>{'Already paid'}</Text>
                    <Text style={{ color: '#333333', fontSize: 13 }}>{`$${paid.toFixed(2)}`}</Text>
                  </View>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                    <Text style={{ color: '#333333', fontSize: 14, fontWeight: '700' }}>{remaining >= 0 ? 'Outstanding balance' : 'Credit'}</Text>
                    <Text style={{ color: remaining > 0 ? '#B8393C' : '#28A745', fontSize: 14, fontWeight: '700' }}>{`$${Math.abs(remaining).toFixed(2)}`}</Text>
                  </View>
                  {Math.abs(a) >= 0.005 && (
                    <Text style={{ color: '#B8393C', fontSize: 12, fontWeight: '600', marginTop: 8 }}>
                      {`${a >= 0 ? 'Additional charge: +' : 'Reduction: -'}$${Math.abs(a).toFixed(2)}`}
                    </Text>
                  )}
                </>
              )}
            </View>
          </View>
        );
      })()}
                </View>
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>
    </View>
  );
};

export default VehicleScheduleEditor;
